'use client'

import { useEffect, useMemo, useState } from 'react'
import { useRouter } from 'next/navigation'
import { createBrowserSupabaseClient } from '@/lib/supabaseClient'
import SeparationCalculator from '@/app/components/calculators/SeparationCalculator'
import PCSCalculator from '@/app/components/calculators/PCSCalculator'
import VeteranCalculator from '@/app/components/calculators/VeteranCalculator'
import type { CalculatorFormData } from '@/app/Calculator'

type NormalizedProfile = {
  status: string | null
  next_mission: string | null
  is_spouse_managing_transition: boolean | null
  rank: string | null
  years_of_service: number | null
  current_zip: string | null
  target_zip: string | null
  gaining_zip: string | null
  rnltd_date: string | null
  physical_move_date: string | null
  has_spouse: boolean | null
  child_count: number | null
  current_civilian_salary: number | null
  va_disability_percent: number | null
}

type CalculatorType = 'SEPARATION' | 'PCS' | 'VETERAN'

const normalizeStatus = (value: string | null) => {
  const cleaned = value?.toLowerCase() ?? ''
  if (cleaned.includes('active') && cleaned.includes('duty')) return 'active_duty'
  if (cleaned.includes('reserv')) return 'reservist'
  if (cleaned.includes('prospect') || cleaned.includes('thinking') || cleaned.includes('joining')) return 'prospect'
  if (cleaned.includes('spouse')) return 'spouse'
  if (cleaned.includes('vet')) return 'veteran'
  return cleaned.replace(/[\s-]+/g, '_')
}

const normalizeMission = (value: string | null) => {
  const cleaned = value?.toLowerCase() ?? ''
  if (cleaned.includes('pcs')) return 'pcs'
  if (cleaned.includes('separ') || cleaned.includes('retir')) return 'separation'
  return cleaned.replace(/[\s-]+/g, '_')
}

const isTruthy = (value: unknown) => {
  if (typeof value === 'boolean') return value
  if (typeof value === 'string') return value.toLowerCase() === 'yes' || value === 'true'
  return false
}

const getCalculatorType = (profile: NormalizedProfile): CalculatorType => {
  const status = normalizeStatus(profile.status)
  const mission = normalizeMission(profile.next_mission)
  const isSpouseManaging = isTruthy(profile.is_spouse_managing_transition)
  const hasMilitarySignals = Boolean(
    profile.rank ||
      typeof profile.years_of_service === 'number' ||
      mission ||
      profile.gaining_zip ||
      profile.rnltd_date
  )
  if (
    status === 'active_duty' ||
    status === 'reservist' ||
    (status === 'spouse' && isSpouseManaging) ||
    (!status && hasMilitarySignals)
  ) {
    return mission === 'pcs' || profile.gaining_zip ? 'PCS' : 'SEPARATION'
  }
  return 'VETERAN'
}

export default function DashboardCalculator() {
  const router = useRouter()
  const supabase = useMemo(() => createBrowserSupabaseClient(), [])
  const [profile, setProfile] = useState<NormalizedProfile | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')

  useEffect(() => {
    let active = true
    const loadProfile = async () => {
      const { data } = await supabase.auth.getSession()
      if (!data.session) {
        router.push('/login')
        return
      }

      const { data: profileData, error: profileError } = await supabase
        .from('profiles')
        .select(
          [
            'status',
            'next_mission',
            'is_spouse_managing_transition',
            'rank',
            'years_of_service',
            'current_zip',
            'target_zip',
            'gaining_zip',
            'rnltd_date',
            'physical_move_date',
            'has_spouse',
            'child_count',
            'current_civilian_salary',
            'va_disability_percent'
          ].join(',')
        )
        .eq('id', data.session.user.id)
        .single()

      if (!active) return
      if (profileError) {
        setError('Unable to load your profile. Please try again.')
        setLoading(false)
        return
      }

      const profileObject: Partial<NormalizedProfile> =
        profileData && typeof profileData === 'object'
          ? (profileData as Partial<NormalizedProfile>)
          : {}

      const normalized: NormalizedProfile = {
        status: profileObject.status ?? null,
        next_mission: profileObject.next_mission ?? null,
        is_spouse_managing_transition:
          profileObject.is_spouse_managing_transition ?? null,
        rank: profileObject.rank ?? null,
        years_of_service:
          typeof profileObject.years_of_service === 'number'
            ? profileObject.years_of_service
            : null,
        current_zip: profileObject.current_zip ?? null,
        target_zip: profileObject.target_zip ?? null,
        gaining_zip: profileObject.gaining_zip ?? null,
        rnltd_date: profileObject.rnltd_date ?? null,
        physical_move_date: profileObject.physical_move_date ?? null,
        has_spouse: profileObject.has_spouse ?? null,
        child_count:
          typeof profileObject.child_count === 'number' ? profileObject.child_count : null,
        current_civilian_salary:
          typeof profileObject.current_civilian_salary === 'number'
            ? profileObject.current_civilian_salary
            : null,
        va_disability_percent:
          typeof profileObject.va_disability_percent === 'number'
            ? profileObject.va_disability_percent
            : null
      }

      setProfile(normalized)
      setLoading(false)
    }

    loadProfile()

    return () => {
      active = false
    }
  }, [router, supabase])

  if (loading) {
    return <p className="px-4 text-sm text-slate-500">Loading your calculator...</p>
  }

  if (!profile) {
    return <p className="px-4 text-sm text-rose-500">{error || 'Missing profile.'}</p>
  }

  const dependents =
    Boolean(profile.has_spouse) || (profile.child_count ?? 0) > 0

  const baseValues: Partial<CalculatorFormData> = {
    ...(profile.rank ? { rank: profile.rank } : {}),
    ...(typeof profile.years_of_service === 'number'
      ? { yearsOfService: profile.years_of_service }
      : {}),
    ...(profile.current_zip ? { currentZip: profile.current_zip } : {}),
    hasDependents: dependents
  }

  const calculatorType = getCalculatorType(profile)
  const calculatorLabel =
    calculatorType === 'PCS'
      ? 'PCS Scenario'
      : calculatorType === 'SEPARATION'
      ? 'Separation Scenario'
      : 'Veteran Scenario'

  if (calculatorType === 'PCS') {
    const initialValues: Partial<CalculatorFormData> = {
      ...baseValues,
      ...(profile.gaining_zip ? { targetZip: profile.gaining_zip } : {})
    }
    return (
      <div className="space-y-4">
        <div className="px-4 sm:px-0">
          <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
            Calculator Type
          </p>
          <p className="text-lg font-bold text-slate-900">{calculatorLabel}</p>
        </div>
        <PCSCalculator initialValues={initialValues} />
      </div>
    )
  }

  if (calculatorType === 'SEPARATION') {
    const initialValues: Partial<CalculatorFormData> = {
      ...baseValues,
      ...(profile.target_zip ? { targetZip: profile.target_zip } : {})
    }
    return (
      <div className="space-y-4">
        <div className="px-4 sm:px-0">
          <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
            Calculator Type
          </p>
          <p className="text-lg font-bold text-slate-900">{calculatorLabel}</p>
        </div>
        <SeparationCalculator initialValues={initialValues} />
      </div>
    )
  }

  const veteranValues: Partial<CalculatorFormData> = {
    ...baseValues,
    ...(profile.target_zip ? { targetZip: profile.target_zip } : {}),
    ...(typeof profile.current_civilian_salary === 'number'
      ? { currentSalary: profile.current_civilian_salary }
      : {})
  }
  return (
    <div className="space-y-4">
      <div className="px-4 sm:px-0">
        <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
          Calculator Type
        </p>
        <p className="text-lg font-bold text-slate-900">{calculatorLabel}</p>
      </div>
      <VeteranCalculator initialValues={veteranValues} />
    </div>
  )
}
